/**
 * Utilities for managing and cleaning up localStorage for quote drafts
 */

interface QuoteDraft {
	quoteId: string;
	lastModified: string;
	isDirty: boolean;
	[key: string]: any;
}

const STALE_THRESHOLD = 7 * 24 * 60 * 60 * 1000;
const QUOTE_DRAFT_PREFIX = 'quote_';
const QUOTE_DRAFT_SUFFIX = '_draft';

export const cleanupStaleQuoteDrafts = (): number => {
	let cleanedCount = 0;
	const keys = Object.keys(localStorage);

	keys.forEach((key) => {
		if (
			!key.startsWith(QUOTE_DRAFT_PREFIX) ||
			!key.endsWith(QUOTE_DRAFT_SUFFIX)
		) {
			return;
		}

		try {
			const stored = localStorage.getItem(key);
			if (!stored) return;

			const draft: QuoteDraft = JSON.parse(stored);
			const lastModified = new Date(draft.lastModified).getTime();
			const age = Date.now() - lastModified;

			if (age > STALE_THRESHOLD) {
				localStorage.removeItem(key);
				cleanedCount++;
			}
		} catch (error) {
			localStorage.removeItem(key);
			cleanedCount++;
		}
	});

	if (cleanedCount > 0) {
	}

	return cleanedCount;
};

export const clearAllQuoteDraftsExcept = (keepQuoteId: string): number => {
	let clearedCount = 0;
	const keepKey = `${QUOTE_DRAFT_PREFIX}${keepQuoteId}${QUOTE_DRAFT_SUFFIX}`;
	const keys = Object.keys(localStorage);

	keys.forEach((key) => {
		if (
			!key.startsWith(QUOTE_DRAFT_PREFIX) ||
			!key.endsWith(QUOTE_DRAFT_SUFFIX)
		) {
			return;
		}

		if (key !== keepKey) {
			localStorage.removeItem(key);
			clearedCount++;
		}
	});

	return clearedCount;
};

export const clearAllQuoteDrafts = (): number => {
	let clearedCount = 0;
	const keys = Object.keys(localStorage);

	keys.forEach((key) => {
		if (
			key.startsWith(QUOTE_DRAFT_PREFIX) &&
			key.endsWith(QUOTE_DRAFT_SUFFIX)
		) {
			localStorage.removeItem(key);
			clearedCount++;
		}
	});

	return clearedCount;
};

const getOldestQuoteDraft = (): string | null => {
	const keys = Object.keys(localStorage);
	let oldestKey: string | null = null;
	let oldestTime = Date.now();

	keys.forEach((key) => {
		if (
			!key.startsWith(QUOTE_DRAFT_PREFIX) ||
			!key.endsWith(QUOTE_DRAFT_SUFFIX)
		) {
			return;
		}

		try {
			const stored = localStorage.getItem(key);
			if (!stored) return;

			const draft: QuoteDraft = JSON.parse(stored);
			const lastModified = new Date(draft.lastModified).getTime();

			if (lastModified < oldestTime) {
				oldestTime = lastModified;
				oldestKey = key;
			}
		} catch (error) {}
	});

	return oldestKey;
};

/**
 * Save to localStorage with quota management
 * If quota is exceeded, clear old drafts and retry
 */
export const saveWithQuotaManagement = (
	key: string,
	data: any,
	currentQuoteId: string
): boolean => {
	try {
		localStorage.setItem(key, JSON.stringify(data));
		return true;
	} catch (error) {
		if (error.name === 'QuotaExceededError') {
			const staleCount = cleanupStaleQuoteDrafts();

			if (staleCount > 0) {
				try {
					localStorage.setItem(key, JSON.stringify(data));
					return true;
				} catch (e) {}
			}

			const oldestKey = getOldestQuoteDraft();
			if (oldestKey && oldestKey !== key) {
				localStorage.removeItem(oldestKey);

				try {
					localStorage.setItem(key, JSON.stringify(data));
					return true;
				} catch (e) {}
			}

			clearAllQuoteDraftsExcept(currentQuoteId);

			try {
				localStorage.setItem(key, JSON.stringify(data));
				return true;
			} catch (e) {
				return false;
			}
		} else {
			return false;
		}
	}
};

export const getQuoteDraftsSize = (): number => {
	let totalSize = 0;
	const keys = Object.keys(localStorage);

	keys.forEach((key) => {
		if (
			key.startsWith(QUOTE_DRAFT_PREFIX) &&
			key.endsWith(QUOTE_DRAFT_SUFFIX)
		) {
			const value = localStorage.getItem(key);
			if (value) {
				totalSize += (key.length + value.length) * 2;
			}
		}
	});

	return totalSize;
};

export const formatBytes = (bytes: number): string => {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};
